import { Container } from "unstated";
import moment from "moment";
import { fetchSpaces } from "../services/srpService";
import { scrollToPage } from "../helpers/scrollTo";
import { dataLayer } from "../helpers/dataLayer";
import updateUrlForQuery, { getUrlForQuery } from "../helpers/updateUrl";
import { SpaceShapeIn } from "../transpose";
import { reportError } from "venuescanner-js/react/services/errorService";

const chunkMapResults = (mapResults = [], chunkSize) =>
  mapResults.reduce((resultArray, item, index) => {
    const chunkIndex = Math.floor(index / chunkSize);

    if (!resultArray[chunkIndex]) {
      resultArray[chunkIndex] = [];
    }

    resultArray[chunkIndex].push(item);

    return resultArray;
  }, []);

const getMapPagesToShow = (page, numberOfPages) => {
  if (page === 1) return [1, 2, 3];
  if (page === numberOfPages)
    return [numberOfPages - 2, numberOfPages - 1, numberOfPages];
  return [page - 1, page, page + 1];
};

const parseData = (data) => {
  const { spaces = [], map_results: mapResults, limit } = data;
  const page = parseInt(data.page, 10);
  const chunkedMapResults = chunkMapResults(mapResults, limit);
  const firstMapPage = page === 1 ? 1 : page - 1;

  return {
    ...data,
    spaces: {
      [page]: spaces?.map(SpaceShapeIn),
    },
    map_results: chunkedMapResults.reduce((acc, chunk, i) => {
      acc[i + firstMapPage] = chunk;
      return acc;
    }, {}),
  };
};

export default class SrpContainer extends Container {
  state = {
    isLoading: false,
    selectedSpaceID: null,
    pageInViewport: 1,
  };
  map = null;
  constructor(initialState) {
    super();
    if (typeof initialState === "object") {
      const { data } = initialState;
      const page = parseInt(data.page, 10);

      const {
        capacity,
        budget_min: budgetMin,
        budget_max: budgetMax,
        layout,
        type,
        attr,
        style,
        keywords,
        start_date: startDate,
        end_date: endDate,
        start_time: startTime,
        end_time: endTime,
        ...query
      } = initialState.query;
      // console.log({query:initialState.query})
      this.state = {
        ...this.state,
        ...initialState,
        query: {
          ...query,
          capacity: capacity && parseInt(capacity, 10),
          budget: budgetMin
            ? [budgetMin, budgetMax].filter(Boolean).map((n) => parseInt(n, 10))
            : undefined,
          layout: layout?.split(",").filter(Boolean),
          type: type?.split(",").filter(Boolean),
          attrs: attr?.split(",").filter(Boolean),
          styles: style?.split(",").filter(Boolean),
          keywords: keywords?.split(",").filter(Boolean),
          map_pages: data ? getMapPagesToShow(page, data.pages) : [],
          dates: {
            start: startDate
              ? moment(startDate, "DD/MM/YYYY").toISOString()
              : null,
            end: endDate ? moment(endDate, "DD/MM/YYYY").toISOString() : null,
          },
          times: {
            start: startTime,
            end: endTime,
          },
          sub_plan: initialState.query.sub_plan
            ? new Set(initialState.query.sub_plan.split(",").filter(Boolean))
            : undefined,
          radius: (initialState.query.radius || 0).toString(),
        },
        data: parseData(data),
        pageInViewport: page,
      };
    }
  }

  get isAdmin() {
    return this.state.isAdmin;
  }

  get numberOfResults() {
    return Object.values(this.state.data.spaces).reduce(
      (sum, spaces) => sum + spaces.length,
      0
    );
  }

  get hasValidLocation() {
    const { query, data } = this.state;
    if (query.new_location?.label && query.new_location?.value) return true;
    return query.city_slug && data.place_model;
  }

  loadPage = async (page, goTo = true) => {
    this.setAsDynamic();
    await this.patchQuery({ page });

    if (this.pageIsLoaded(page)) {
      scrollToPage(page);
      return;
    }

    await this.refresh({ goTo });
  };

  refresh = async ({ replace = false, goTo = true } = {}) => {
    await this.setState({ isLoading: true });

    if (!window) return;
    // If not on an SRP, redirect to one
    if (
      !/^\/book-venue-/.test(window.location.pathname) &&
      !/^\/search/.test(window.location.pathname)
    ) {
      window.location = getUrlForQuery(this.state.query);
      return;
    }

    if (replace) await this.patchQuery({ map_pages: [1, 2, 3] });

    updateUrlForQuery(this.state.query);
    try {
      const {
        no_results: noResults,
        sort_by: sortBy,
        ...data
      } = await fetchSpaces(this.state.query);
      this.setAsDynamic();
      const {
        page = 1,
        map_results: mapResults = [],
        default_limit: defaultLimit = 36,
      } = data;
      const spaces = data.spaces?.map(SpaceShapeIn) || [];
      dataLayer({ spaces });
      let nextData = {};
      if (replace) {
        nextData = {
          ...this.state.data,
          ...parseData(data),
        };
      } else {
        const numberOfPagesReturned = Math.ceil(spaces.length / defaultLimit);
        const pages = [...Array(numberOfPagesReturned).keys()].reduce(
          (acc, p) => {
            acc[p + page] = spaces.slice(
              p * defaultLimit,
              (p + 1) * defaultLimit
            );
            return acc;
          },
          {}
        );

        nextData = {
          ...this.state.data,
          ...data,
          spaces: {
            ...this.state.data.spaces,
            ...pages,
          },
        };
        const mapPage = this.state.query.map_pages[0];
        const chunkedMapResults = chunkMapResults(
          mapResults,
          this.state.query.limit
        ).reduce((acc, chunk, i) => {
          acc[i + mapPage] = chunk;
          return acc;
        }, {});

        nextData.map_results = {
          ...this.state.data.map_results,
          ...chunkedMapResults,
        };
      }

      await this.setState({
        data: nextData,
        noResults,
        query: {
          ...this.state.query,
          sort: sortBy,
        },
        isLoading: false,
      });

      if (goTo) scrollToPage(page);

      if (typeof document !== "undefined") {
        document.title = document.title.replace(
          /[^|]+|/,
          nextData.seo.header + " "
        );
      }

      return data;
    } catch (err) {
      console.error("Error fetching spaces", err);
      reportError("SrpContainer", err);
      await this.setState({ isLoading: false });
    }
  };

  setAsDynamic = () => {
    this.setState({ dynamicSearch: true });
    if (document)
      document.querySelector('meta[name="robots"]').content = "noindex, follow";
  };

  changeSort = async (field) => {
    await this.patchQuery({
      map_pages: [1, 2, 3],
      page: 1,
      sort: field,
    });
    return this.refresh();
  };

  patchQuery = (delta) => {
    let stopOnChangeUpdate = true;
    if (!stopOnChangeUpdate) {
    }
    const { map_results: mapResults, pages } = this.state.data;

    const nextQuery = {
      ...this.state.query,
      ...delta,
    };

    if (!delta.map_pages) {
      const mapPagesToShow = getMapPagesToShow(
        parseInt(delta.page || this.state.query.page, 10),
        pages
      );
      const pagesLoaded = Object.keys(mapResults);

      nextQuery.map_pages = mapPagesToShow.filter(
        (p) => !pagesLoaded.includes(`${p}`)
      );
    }
    if (JSON.stringify(nextQuery) === JSON.stringify(this.state.query)) return;

    return this.setState({ query: nextQuery });
  };

  get filters() {
    return this.state.query;
  }

  get hasFilters() {
    return [
      "capacity",
      "layout",
      "budget_min",
      "budget_max",
      "type",
      "attr",
      "style",
      "keywords",
    ].some((k) => {
      const v = this.state.query[k];
      return Array.isArray(v) ? v.length > 0 : !!v;
    });
  }

  clearFilters = async (refresh = true) => {
    await this.patchQuery({
      page: 1,
      capacity: undefined,
      budget: undefined,
      layout: undefined,
      type: undefined,
      attrs: undefined,
      styles: undefined,
      keywords: undefined,
    });

    if (refresh) await this.refresh();
  };

  selectSpace = (spaceID) => this.setState({ selectedSpaceID: spaceID });

  deselectSpace = () => {
    if (this.state.selectedSpaceID === null) return;
    return this.setState({ selectedSpaceID: null });
  };

  pageIsLoaded = (page) =>
    Object.keys(this.state.data.spaces).includes(`${page}`);

  spaceIsLoaded = (spaceID) =>
    Object.values(this.state.data.spaces).some((page) =>
      page.some((s) => s.id === spaceID)
    );

  setPageInViewport = (page) => {
    if (page === this.state.pageInViewport) return;
    updateUrlForQuery({ ...this.state.query, page });
    return this.setState({ pageInViewport: page });
  };

  updateMapBounds = async (region, centre) => {
    await this.patchQuery({
      map_pages: [1, 2, 3],
      page: 1,
      region,
      centre,
    });
    await this.setState({ pageInViewport: 1 });
    return this.refresh({ replace: true });
  };

  setMap = async (map) => {
    this.map = map;
    let pages = Object.keys(this.state.data.spaces);
    if (pages.length === 1) {
      let page = pages.pop();
      dataLayer({ spaces: this.state.data.spaces[page] });
    }
  };

  // spaceIsInShortlist = spaceID => {
  //   // console.log(this.state.data.shortlist, spaceID.toString())
  //   return this.state.data.shortlist.map(id => id.toString()).includes(spaceID.toString())
  // }

  // toggleShortlist = spaceID => {
  //   const { shortlist } = this.state.data

  //   const nextShortlist = shortlist.includes(spaceID.toString())
  //     ? shortlist.filter(sid => sid !== spaceID.toString())
  //     : [...shortlist, spaceID.toString()]
  //   return this.setState({
  //     data: {
  //       ...this.state.data,
  //       shortlist: nextShortlist
  //     }
  //   })
  // }
}
